import React, { useState } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import { ImageOff } from 'lucide-react';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** URL relative ou absolue (passée à getImageUrl si relative) */
  src: string | null | undefined;
  /** Contenu affiché si l'image ne charge pas (erreur ou URL vide) */
  fallback?: React.ReactNode;
  /** Utiliser getImageUrl pour préfixer l'URL par l'API (défaut: true) */
  resolveUrl?: boolean;
}

/**
 * Affiche une image avec repli en cas d'erreur de chargement (404, CORS, réseau).
 * Utilise getImageUrl pour construire l'URL complète vers le backend.
 */
export default function ImageWithFallback({
  src,
  alt = '',
  fallback,
  resolveUrl = true,
  className,
  ...props
}: ImageWithFallbackProps) {
  const [error, setError] = useState(false);
  const resolvedSrc = resolveUrl ? getImageUrl(src) : (src || '');

  if (!resolvedSrc || error) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className || ''}`}
        aria-label={alt || 'Image non disponible'}
      >
        <ImageOff className="w-8 h-8" />
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      onError={() => setError(true)}
      loading="lazy"
      {...props}
    />
  );
}
